import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Link } from 'react-router-dom';
import { Plus, Eye, Calendar, User, Package } from 'lucide-react';
import { purchaseService } from '../api/purchases';
import { supplierService } from '../api/suppliers';
import type { Purchase, Supplier } from '../types';

export default function Purchases() {
  const { formatCurrency } = useCurrency();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [purchasesData, suppliersData] = await Promise.all([
        purchaseService.getAll(),
        supplierService.getAll()
      ]);
      
      setPurchases(purchasesData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Failed to load purchases data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSupplierName = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.name || 'Unknown Supplier';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchases</h1>
          <p className="text-sm text-neutral-600">Record and manage medicine purchases</p>
        </div>
        <Link
          to="/pharmacy/purchases/new"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Purchase
        </Link>
      </header>

      {/* Purchases Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-neutral-900">{purchase.invoiceNo}</div>
                    {purchase.notes && (
                      <div className="text-sm text-neutral-500">{purchase.notes}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-neutral-400 mr-2" />
                      <span className="text-sm text-neutral-900">{getSupplierName(purchase.supplierId)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-neutral-400 mr-2" />
                      <span className="text-sm text-neutral-900">
                        {new Date(purchase.date).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Package className="h-4 w-4 text-neutral-400 mr-2" />
                      <span className="text-sm text-neutral-900">
                        {purchase.items.length} item{purchase.items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900">${purchase.total.toFixed(2)}</div>
                      <div className="text-sm text-neutral-500">
                        Subtotal: ${purchase.subtotal.toFixed(2)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button className="text-blue-600 hover:text-blue-800 transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {purchases.length === 0 && (
            <div className="text-center py-8">
              <p className="text-neutral-500">No purchases found</p>
              <Link
                to="/pharmacy/purchases/new"
                className="text-blue-600 hover:text-blue-800 text-sm mt-2 inline-block"
              >
                Create your first purchase
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
